import { useEffect } from 'react';

import type { AsyncValue } from '../redux/asyncValue';

import useAsyncValue from './useAsyncValue';
import useRequestConnectedToAsyncValue from './useRequestConnectedToAsyncValue';

/**
 * Makes an async request and returns the result as an `AsyncValue`.
 *
 * The request _must_ be a stable reference.  If it changes it will be re-invoked.  Use `useCallback` or `useDebouncedCallback` as appropriate.
 *
 * Things this hook does:
 * - Invokes the callback as an effect.
 * - Re-triggers the effect if the callback changes.
 * - Handles stale requests by ignoring them.
 * - Extracts errors from the response.
 *
 * This hook does not do:
 * - Caching
 * - Debouncing
 *
 * Note that the request function must be stable between re-renders.
 *
 * @example
 * const MyComponent = () => {
 *   const getEndpoint = useCallback(() => get('/api/endpoint'), []);
 *   const { value, isLoading, errors } = useAsyncRequest(() => getEndpoint);
 *
 *   if (isLoading) {
 *     return <Loader />;
 *   }
 *   if (errors) {
 *     return <ErrorList errors={errors} />;
 *   }
 *
 *   return <div>{value}</div>;
 * };
 *
 * @deprecated - Prefer to use the `useQuery`/`useMutation` hooks from `./app/ReactQuery/` instead.
 */
export default function useAsyncRequest<T>(
  request: () => Promise<{ data?: T }>,
): AsyncValue<T> {
  const asyncValue = useAsyncValue<T>();

  const connectedRequest = useRequestConnectedToAsyncValue(request, asyncValue);

  useEffect(() => {
    // useEffect cannot accept the async function directly. It expects the return value be undefined or a clean-up
    // function.  This callback discards the returned promise.
    connectedRequest();
  }, [connectedRequest]);

  return asyncValue;
}
